import { makeStyles } from '@mui/styles';
import Autocomplete from 'apollo-react/components/Autocomplete';
import Typography from 'apollo-react/components/Typography';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { removeWarningByIdAndMessage } from '../../../../actions/WarningActions';
import { useTranslation } from '../../../../custom-hooks/useTranslation';
import {
  AUTOCOMPLETE_NO_OPTIONS_TEXT,
  AUTOCOMPLETE_SEARCH_DELAY,
} from '../../../../form-designer/constants/runtime.constants';
import { getWarnings } from '../../../../form-designer/selectors/common.selectors';
import { isNilOrEmpty } from '../../../../utils/ramdaUtils';
import {
  autoCompleteRightValue,
  deleteAutocompleteError,
  editEntityInfo,
  getAutoCompleteRightValues,
} from '../../../actions/entity-designer.actions';
import { getEntityProp } from '../../../selectors/entity-designer.selectors';
import { getRightValidationErrorMessage } from '../../../utils/validation.utils';

const handleChangeHelper = (isValueSelectedFromOptions) => {
  isValueSelectedFromOptions.current = true;

  setTimeout(() => {
    isValueSelectedFromOptions.current = false;
  }, 50);
};

const useStyles = makeStyles({
  rowLabel: {
    paddingTop: 8,
    paddingRight: 8,
    alignItems: 'center',
    width: 200,
  },
  rowColumn: {
    flex: 1,
    alignItems: 'center',
    '& div': {
      margin: 0,
    },
    '& [class*="MuiAutocomplete-endAdornment"]': {
      display: 'none',
    },
  },
});

const AutoCompleteTextBox = ({
  prop,
  label,
  disabled,
  setIsAutoCompleteLoading,
  isAutoCompleteLoading,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const value = useSelector((state) => getEntityProp(state, { prop }));
  const translations = useSelector((state) => state.translations);
  const errors = useSelector(getWarnings);
  const hasError = useMemo(() => errors.some((error) => error.instanceId === prop), [errors, prop]);

  const [noOptionsText, setNoOptionsText] = useState(AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH);
  const [localValue, setLocalValue] = useState({ label: value, value });
  const [options, setOptions] = useState([]);

  const timeoutRef = useRef(null);
  const isValueSelectedFromOptions = useRef(false);

  useEffect(() => {
    setLocalValue({ label: value, value });

    if (isNilOrEmpty(value)) {
      setOptions([]);
    }
  }, [value]);

  const handleChange = (_, newValue) => {
    if (!newValue) {
      setNoOptionsText(AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH);
      setOptions([]);

      // clearing the value is handled by handleBlur
      return;
    }

    handleChangeHelper(isValueSelectedFromOptions);

    if (newValue.custom) {
      dispatch(
        autoCompleteRightValue({
          value: newValue.value,
          propName: prop,
          valueToSelect: 'RHT_CODE',
          label,
        })
      );
    } else {
      if (localValue.value !== newValue.value) {
        batch(() => {
          dispatch(deleteAutocompleteError({ instanceId: prop }));
          dispatch(
            removeWarningByIdAndMessage(prop, getRightValidationErrorMessage(label, translations))
          );
          dispatch(editEntityInfo({ prop, value: newValue.value.trim() }));
        });
      }
    }
    setOptions([]);
  };

  const handleInputChange = (_, newValue, reason) => {
    clearTimeout(timeoutRef.current);

    if (reason !== 'input' || isNilOrEmpty(newValue)) {
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      setIsAutoCompleteLoading({ ...isAutoCompleteLoading, [prop]: true });

      const newOptions = await dispatch(
        getAutoCompleteRightValues({
          value: newValue,
          valueToSelect: 'RHT_CODE',
        })
      );

      setIsAutoCompleteLoading({ ...isAutoCompleteLoading, [prop]: false });
      setOptions(newOptions);

      if (!newOptions.length && noOptionsText === AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH) {
        setNoOptionsText(AUTOCOMPLETE_NO_OPTIONS_TEXT.NO_OPTIONS);
      } else if (newOptions.length && noOptionsText === AUTOCOMPLETE_NO_OPTIONS_TEXT.NO_OPTIONS) {
        setNoOptionsText(AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH);
      }
    }, AUTOCOMPLETE_SEARCH_DELAY);
  };

  const handleBlur = async ({ target }) => {
    if (noOptionsText === AUTOCOMPLETE_NO_OPTIONS_TEXT.NO_OPTIONS) {
      setNoOptionsText(AUTOCOMPLETE_NO_OPTIONS_TEXT.TYPE_TO_SEARCH);
    }

    if (isValueSelectedFromOptions.current) {
      return;
    }

    const newValue = target.value;

    if (isNilOrEmpty(newValue) && isNilOrEmpty(value)) {
      return;
    }

    if (newValue !== value) {
      dispatch(
        autoCompleteRightValue({
          value: newValue,
          propName: prop,
          valueToSelect: 'RHT_CODE',
          label,
          setLocalValue,
        })
      );

      setOptions([]);
    }
  };

  return (
    <>
      <div className={classes.rowLabel}>
        <Typography>{translateString(label)}</Typography>
      </div>
      <div className={classes.rowColumn}>
        <Autocomplete
          fullWidth
          size="small"
          noOptionsText={translateString(noOptionsText)}
          getOptionLabel={(option) =>
            (option.value === localValue.value ? localValue.value : option.label) ?? ''
          }
          error={hasError}
          filterSelectedOptions={false}
          disabled={disabled}
          clearOnBlur={false}
          source={options}
          value={localValue}
          allowAddNewOptions={false}
          onChange={handleChange}
          onInputChange={handleInputChange}
          onBlur={handleBlur}
        />
      </div>
    </>
  );
};

export default AutoCompleteTextBox;
